import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Container, Modal, ModalBody, Pagination, PaginationItem, PaginationLink } from "reactstrap";
import { RefreshCw, X } from "react-feather";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import {
  getStuckOrderTickets,
  refundStuckOrderTicket,
  forceCreateStuckOrderTicket,
} from "../../utils/apiCalls";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Loader as RLoader } from "../reporting/ReportingShared";

/** Who took the money. Named because the refund dialog quotes it back on a money action. */
const GATEWAY_LABEL = {
  moyasar: "Moyasar",
  telr: "Telr",
  nearpay: "NearPay",
};

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "open", label: "Open" },
  { value: "refunded", label: "Refunded" },
  { value: "refund_failed", label: "Refund Failed" },
  { value: "force_created", label: "Force-created" },
  { value: "auto_resolved", label: "Auto-resolved" },
];

const STATUS_COLOR = {
  open: { bg: C.orangeLight, fg: C.orange },
  refunded: { bg: C.greenLight, fg: C.green },
  refund_failed: { bg: C.coralLight, fg: C.coral },
  force_created: { bg: C.blueLight, fg: C.blue },
  auto_resolved: { bg: C.bg, fg: C.textMed },
  refund_requested: { bg: C.orangeLight, fg: C.orange },
};

const Pill = ({ status, label }) => {
  const c = STATUS_COLOR[status] || { bg: C.bg, fg: C.textMed };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "4px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        background: c.bg,
        color: c.fg,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.fg }} />
      {label || (status || "").replace(/_/g, " ").toUpperCase()}
    </span>
  );
};

const ActionBtn = ({ label, onClick, disabled, variant = "primary" }) => {
  const palette =
    variant === "danger"
      ? { bg: C.coral, bgHover: C.coralDark, fg: C.white, border: C.coral }
      : { bg: C.white, bgHover: C.blueLight, fg: C.blue, border: C.blue };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "6px 12px",
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
        background: palette.bg,
        color: palette.fg,
        border: `1.5px solid ${palette.border}`,
        transition: "all 0.15s",
        whiteSpace: "nowrap",
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.background = palette.bgHover;
      }}
      onMouseLeave={(e) => {
        if (!disabled) e.currentTarget.style.background = palette.bg;
      }}
    >
      {label}
    </button>
  );
};

const filterSelectStyle = {
  padding: "7px 12px",
  fontSize: 12,
  borderRadius: 8,
  border: `1.5px solid ${C.border}`,
  fontFamily: "inherit",
  outline: "none",
  color: C.text,
  background: C.white,
  cursor: "pointer",
  minWidth: 150,
};

const StuckOrders = () => {
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector(
    (state) => state.merchant || {}
  );
  const selectedMerchant =
    userType === "merchant" ? merchantId : reduxSelectedMerchant;

  const [isLoading, setIsLoading] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [detailTicket, setDetailTicket] = useState(null);

  const [filters, setFilters] = useState({
    MerchantId: selectedMerchant || "",
    status: "",
    customerRefundRequested: "",
    limit: 20,
  });

  const fetchTickets = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      const data = await getStuckOrderTickets(page, filters.limit, filters);
      if (data) {
        setTickets(data.rows || []);
        setTotalPages(data.totalPages || 1);
      } else {
        setTickets([]);
        setTotalPages(1);
      }
      setIsLoading(false);
    },
    [filters]
  );

  useEffect(() => {
    fetchTickets(currentPage);
  }, [fetchTickets, currentPage]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, MerchantId: selectedMerchant || "" }));
    setCurrentPage(1);
  }, [selectedMerchant]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page);
  };

  const handleRefund = async (ticket) => {
    // A NearPay capture cannot be refunded from here, and the server refuses it outright: NearPay's
    // POS API exposes no reversal endpoint, and the SDK's on-device `reverse()` is not reachable from
    // a server. Saying so up front beats letting somebody click a red button and read an error.
    if (ticket.gateway === "nearpay") {
      window.alert(
        `This card was taken on a screen's own NearPay reader, so it cannot be refunded from here.\n\n` +
          `Reverse it on the terminal itself, then close this ticket.\n\n` +
          `NearPay transaction: ${ticket.moyasarPaymentId}`
      );
      return;
    }

    if (
      !window.confirm(
        `Refund this ${GATEWAY_LABEL[ticket.gateway] ?? "Moyasar"} payment ` +
          `${ticket.moyasarPaymentId} (${ticket.amount ?? ""} ${
            ticket.currency ?? ""
          })? This cannot be undone.`
      )
    )
      return;
    setActingId(ticket.id);
    await refundStuckOrderTicket(ticket.id);
    setActingId(null);
    fetchTickets(currentPage);
  };

  const handleForceCreate = async (ticket) => {
    if (
      !window.confirm(
        `Force-create the order for payment ${ticket.moyasarPaymentId}?`
      )
    )
      return;
    setActingId(ticket.id);
    await forceCreateStuckOrderTicket(ticket.id);
    setActingId(null);
    fetchTickets(currentPage);
  };

  const customerName = (user) =>
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    user?.phoneNo ||
    user?.email ||
    "—";

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1).slice(
    Math.max(0, currentPage - 3),
    Math.min(totalPages, currentPage + 2)
  );

  return (
    <Fragment>
      <Breadcrumb title="Stuck Orders" parent="Orders" />
      <Container fluid={true}>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          <RCard>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 16,
                flexWrap: "wrap",
              }}
            >
              <div
                onClick={() => fetchTickets(currentPage)}
                title="Refresh"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  border: `1.5px solid ${C.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  background: C.white,
                  flexShrink: 0,
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = C.coral;
                  e.currentTarget.style.background = C.coralLight;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = C.border;
                  e.currentTarget.style.background = C.white;
                }}
              >
                <RefreshCw size={15} color={C.textMed} />
              </div>

              <select
                value={filters.status}
                onChange={(e) => {
                  setFilters({ ...filters, status: e.target.value });
                  setCurrentPage(1);
                }}
                style={filterSelectStyle}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>

              <select
                value={filters.customerRefundRequested}
                onChange={(e) => {
                  setFilters({ ...filters, customerRefundRequested: e.target.value });
                  setCurrentPage(1);
                }}
                style={filterSelectStyle}
              >
                <option value="">All Requests</option>
                <option value="true">Refund Requested</option>
                <option value="false">No Request</option>
              </select>

              <span
                style={{
                  marginLeft: "auto",
                  fontSize: 12,
                  color: C.textLight,
                  background: C.bg,
                  padding: "4px 12px",
                  borderRadius: 20,
                }}
              >
                {tickets?.length || 0} tickets
              </span>
            </div>

            {isLoading ? (
              <RLoader />
            ) : (
              <div
                style={{
                  overflowX: "auto",
                  borderRadius: 10,
                  border: `1px solid ${C.border}`,
                }}
              >
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      <th style={tbl.th}>ID</th>
                      <th style={tbl.th}>Created</th>
                      <th style={tbl.th}>Payment Ref</th>
                      <th style={tbl.th}>Amount</th>
                      <th style={tbl.th}>Customer</th>
                      <th style={tbl.th}>Branch</th>
                      <th style={tbl.th}>Reason</th>
                      <th style={tbl.th}>Refund Req.</th>
                      <th style={tbl.th}>Status</th>
                      <th style={{ ...tbl.th, textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets?.length > 0 ? (
                      tickets.map((ticket) => {
                        const closed =
                          ticket.status === "refunded" ||
                          ticket.status === "force_created" ||
                          ticket.status === "auto_resolved";
                        const busy = actingId === ticket.id;
                        return (
                          <tr key={ticket.id} className="rpt-row">
                            <td
                              style={{
                                ...tbl.td,
                                color: C.coral,
                                fontWeight: 700,
                                cursor: "pointer",
                              }}
                              onClick={() => setDetailTicket(ticket)}
                              title="View ticket details"
                            >
                              #{ticket.id}
                            </td>
                            <td style={tbl.td}>{formatDate(ticket.createdAt)}</td>
                            <td style={{ ...tbl.td, fontWeight: 600 }}>
                              {ticket.moyasarPaymentId}
                            </td>
                            <td style={tbl.td}>
                              {ticket.amount ?? "—"} {ticket.currency ?? ""}
                            </td>
                            <td style={tbl.td}>{customerName(ticket.User)}</td>
                            <td style={tbl.td}>{ticket.Branch?.name || "—"}</td>
                            <td style={{ ...tbl.td, maxWidth: 220, color: C.textMed }}>
                              {ticket.failureReason || "—"}
                            </td>
                            <td style={tbl.td}>
                              {ticket.customerRefundRequested ? (
                                <Pill status="refund_requested" label="REQUESTED" />
                              ) : (
                                "—"
                              )}
                            </td>
                            <td style={tbl.td}>
                              <Pill status={ticket.status} />
                            </td>
                            <td style={tbl.td}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  gap: 8,
                                }}
                              >
                                <ActionBtn
                                  label={busy ? "..." : "Refund"}
                                  variant="danger"
                                  disabled={closed || busy}
                                  onClick={() => handleRefund(ticket)}
                                />
                                <ActionBtn
                                  label="Force-create"
                                  disabled={closed || busy || !ticket.orderPayload}
                                  onClick={() => handleForceCreate(ticket)}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan="10"
                          style={{
                            ...tbl.td,
                            textAlign: "center",
                            padding: 40,
                            color: C.textLight,
                          }}
                        >
                          No stuck orders found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {tickets?.length > 0 && totalPages > 1 && (
              <Pagination className="d-flex justify-content-end mt-3 mb-0">
                <PaginationItem disabled={currentPage <= 1}>
                  <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
                </PaginationItem>
                {pageNumbers.map((i) => (
                  <PaginationItem active={i === currentPage} key={i}>
                    <PaginationLink onClick={() => handlePageChange(i)}>{i}</PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem disabled={currentPage >= totalPages}>
                  <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
                </PaginationItem>
              </Pagination>
            )}
          </RCard>
        </div>

        <TicketDetailModal
          ticket={detailTicket}
          onClose={() => setDetailTicket(null)}
          customerName={customerName}
        />
      </Container>
    </Fragment>
  );
};

const DetailRow = ({ label, value, mono }) => (
  <div
    style={{
      display: "flex",
      gap: 12,
      padding: "9px 0",
      borderBottom: `1px solid ${C.borderLight}`,
    }}
  >
    <div style={{ flex: "0 0 150px", fontSize: 12, fontWeight: 600, color: C.textMed }}>
      {label}
    </div>
    <div
      style={{
        flex: 1,
        fontSize: 13,
        color: C.text,
        wordBreak: "break-word",
        fontFamily: mono ? "monospace" : "inherit",
      }}
    >
      {value === null || value === undefined || value === "" ? "—" : value}
    </div>
  </div>
);

const JsonBlock = ({ data }) => (
  <pre
    style={{
      margin: 0,
      padding: 12,
      background: C.bg,
      border: `1px solid ${C.border}`,
      borderRadius: 8,
      fontSize: 11,
      lineHeight: 1.5,
      color: C.text,
      maxHeight: 240,
      overflow: "auto",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    }}
  >
    {(() => {
      try {
        return JSON.stringify(typeof data === "string" ? JSON.parse(data) : data, null, 2);
      } catch (e) {
        return String(data);
      }
    })()}
  </pre>
);

const TicketDetailModal = ({ ticket, onClose, customerName }) => {
  if (!ticket) return null;
  return (
    <Modal isOpen={!!ticket} toggle={onClose} centered size="lg">
      <style>{reportingGlobalCSS}</style>
      <ModalBody style={{ padding: 0 }} className="rpt-wrap">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 22px",
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: C.text }}>
              Ticket #{ticket.id}
            </span>
            <Pill status={ticket.status} />
          </div>
          <div
            onClick={onClose}
            style={{ cursor: "pointer", color: C.textMed, display: "flex" }}
            title="Close"
          >
            <X size={20} />
          </div>
        </div>

        <div style={{ padding: "12px 22px 22px" }}>
          <SectionTitle title="Order" />
          <DetailRow
            label="Order ID"
            value={
              ticket.orderId ? (
                <span style={{ fontWeight: 700, color: C.green }}>#{ticket.orderId}</span>
              ) : (
                <span style={{ color: C.textLight }}>No order created yet</span>
              )
            }
          />
          <DetailRow label="External ID" value={ticket.externalId} mono />
          <DetailRow label="Created" value={formatDate(ticket.createdAt)} />
          <DetailRow label="Resolved" value={ticket.resolvedAt ? formatDate(ticket.resolvedAt) : null} />

          <SectionTitle title="Payment" />
          <DetailRow label="Gateway" value={ticket.gateway || "moyasar"} />
          <DetailRow label="Payment Ref" value={ticket.moyasarPaymentId} mono />
          <DetailRow label="Amount" value={`${ticket.amount ?? "—"} ${ticket.currency ?? ""}`} />

          <SectionTitle title="Customer" />
          <DetailRow label="Customer" value={customerName(ticket.User)} />
          <DetailRow label="Phone" value={ticket.User?.phoneNo} />
          <DetailRow label="Email" value={ticket.User?.email} />
          <DetailRow label="Branch" value={ticket.Branch?.name} />

          <SectionTitle title="Status" />
          <DetailRow label="Failure reason" value={ticket.failureReason} />
          <DetailRow
            label="Refund requested"
            value={
              ticket.customerRefundRequested
                ? `Yes${ticket.customerRefundRequestedAt ? ` — ${formatDate(ticket.customerRefundRequestedAt)}` : ""}`
                : "No"
            }
          />
          <DetailRow label="Resolved by" value={ticket.resolvedByUserId ? `User #${ticket.resolvedByUserId}` : null} />
          <DetailRow label="Notes" value={ticket.notes} />

          {ticket.orderPayload ? (
            <>
              <SectionTitle title="Stored order payload" />
              <JsonBlock data={ticket.orderPayload} />
            </>
          ) : null}

          {ticket.refundInfo ? (
            <>
              <SectionTitle title="Refund response" />
              <JsonBlock data={ticket.refundInfo} />
            </>
          ) : null}
        </div>
      </ModalBody>
    </Modal>
  );
};

const SectionTitle = ({ title }) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      color: C.coral,
      marginTop: 18,
      marginBottom: 4,
    }}
  >
    {title}
  </div>
);

const formatDate = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "—";
  return (
    date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }) +
    ", " +
    date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    })
  );
};

export default StuckOrders;
